import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type { PayrollRow } from '@aeris/contracts'
import { parseAmount } from '@aeris/finance'
import { compare, formatMoney, isPositive } from '@aeris/money'
import {
  Button,
  CheckboxField,
  DateField,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  InlineAlert,
  Input,
  MoneyInput,
  RadioGroup,
  Select,
} from '@aeris/ui'

import { periodLabel } from '../lib/api/commissions.ts'
import {
  createExpenseCategory,
  fetchEmployees,
  fetchExpenseCategories,
  recordExpense,
} from '../lib/api/expenses.ts'
import { ApiError } from '../lib/api.ts'

/**
 * Lançar um gasto, ou pagar o salário de alguém (F146).
 *
 * Um diálogo só para as duas coisas, porque para quem preenche é a mesma: um
 * valor, de onde saiu o dinheiro, e o que foi. O pagamento de salário vem com
 * o funcionário escolhido e o que falta já no valor — pagar parte é só
 * apagar e digitar menos.
 *
 * As travas daqui são para explicar antes; o servidor confere tudo de novo sob
 * a trava do funcionário e da gaveta.
 */

export type ExpenseDialogMode =
  | { readonly kind: 'expense' }
  | { readonly kind: 'salary'; readonly row: PayrollRow }

export type CashUnavailable = 'no-terminal' | 'no-session' | null

const money = (wire: string): string => formatMoney(parseAmount(wire))

const pad = (value: number): string => String(value).padStart(2, '0')

/** Hoje, no relógio de quem está lançando: `2026-09-14`. */
const today = (): string => {
  const now = new Date()
  return `${String(now.getFullYear())}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

export const ExpenseEntryDialog = ({
  mode,
  period,
  payrollRows,
  cashSessionId,
  cashUnavailable,
  canManage,
  canSeePayroll,
  onClose,
}: {
  mode: ExpenseDialogMode
  period: string
  payrollRows: readonly PayrollRow[]
  cashSessionId: string | null
  cashUnavailable: CashUnavailable
  canManage: boolean
  /**
   * Quem NÃO pode ver a folha ainda lança a marmita que desconta — F157.
   *
   * O caixa continua com o caso que deu nome à F146; o que ele deixa de ver é
   * **quanto falta**, porque isso é o salário da pessoa dito em voz alta. Sem
   * este sinal a tela trataria "não sei" como "não tem salário" e bloquearia o
   * lançamento — consertar o sigilo quebrando a loja.
   */
  canSeePayroll: boolean
  onClose: () => void
}): ReactNode => {
  const queryClient = useQueryClient()
  const salary = mode.kind === 'salary' ? mode.row : null

  const [amount, setAmount] = useState(
    salary !== null && salary.remainingAmount !== null
      ? salary.remainingAmount
      : ''
  )
  const [description, setDescription] = useState(
    salary === null ? '' : `Salário de ${periodLabel(period)}`
  )
  const [categoryId, setCategoryId] = useState('')
  const [newCategory, setNewCategory] = useState('')
  const [employeeId, setEmployeeId] = useState(salary?.employeeId ?? '')
  const [deducts, setDeducts] = useState(false)
  const [spentOn, setSpentOn] = useState(today())
  const [source, setSource] = useState<'cash' | 'other' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const categories = useQuery({
    queryKey: ['expenses', 'categories'],
    queryFn: ({ signal }) => fetchExpenseCategories(signal),
    enabled: salary === null,
  })
  const employees = useQuery({
    queryKey: ['expenses', 'employees', period],
    queryFn: ({ signal }) => fetchEmployees(period, signal),
    enabled: salary === null,
  })

  const activeCategories = (categories.data?.categories ?? []).filter(
    (row) => row.status === 'active'
  )
  const activeEmployees = (employees.data?.employees ?? []).filter(
    (row) => row.status === 'active'
  )

  const deducting = salary !== null || (deducts && employeeId !== '')
  const payrollRow =
    salary ?? payrollRows.find((row) => row.employeeId === employeeId) ?? null
  const employeeName =
    payrollRow?.name ??
    activeEmployees.find((row) => row.id === employeeId)?.name ??
    ''

  const typed = amount === '' ? null : parseAmount(amount)
  /**
   * O que falta pagar a quem o lançamento desconta, quando desconta — e quando
   * quem está olhando pode saber. Sem `payroll.read` fica `null`, e quem
   * decide passa a ser só o servidor.
   */
  const remaining =
    deducting && canSeePayroll ? (payrollRow?.remainingAmount ?? null) : null

  /**
   * O que mostrar ao lado de "descontar do salário", ANTES de marcar.
   *
   * É o número que ajuda a decidir se marca, então não pode depender de já ter
   * marcado. Some por completo para quem não pode ver a folha.
   */
  const remainingLabel = canSeePayroll
    ? (payrollRow?.remainingAmount ?? null)
    : null

  const cashBlocked = source === 'cash' && cashSessionId === null

  const blocker =
    typed === null || !isPositive(typed)
      ? 'Informe o valor.'
      : description.trim() === ''
        ? 'Diga o que foi.'
        : salary === null && categoryId === ''
          ? 'Escolha a categoria.'
          : deducting && canSeePayroll && payrollRow?.dueAmount == null
            ? `${employeeName || 'Este funcionário'} não tem salário neste mês para descontar.`
            : remaining !== null && compare(typed, parseAmount(remaining)) > 0
              ? `Falta pagar ${money(remaining)} a ${employeeName} neste mês.`
              : source === null
                ? 'Diga de onde saiu o dinheiro.'
                : cashBlocked
                  ? cashUnavailable === 'no-terminal'
                    ? 'Este computador ainda não disse em qual caixa está. Escolha o caixa na tela Caixa, ou marque Pix/banco/outro.'
                    : 'Não há caixa aberto neste terminal. Abra o caixa, ou marque Pix/banco/outro.'
                  : spentOn === ''
                    ? 'Escolha o dia.'
                    : null

  const addCategory = useMutation({
    mutationFn: async () =>
      await createExpenseCategory({ name: newCategory.trim() }),
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: ['expenses', 'categories'],
      })
      setCategoryId(response.category.id)
      setNewCategory('')
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível criar a categoria.'
      )
    },
  })

  const save = useMutation({
    mutationFn: async () =>
      await recordExpense({
        kind: salary === null ? 'expense' : 'salary',
        categoryId: salary === null ? categoryId : null,
        employeeId: employeeId === '' ? null : employeeId,
        deductsSalary: salary === null && deducting,
        period: deducting ? period : null,
        description: description.trim(),
        amount,
        spentOn,
        paymentSource: source ?? 'other',
        cashSessionId: source === 'cash' ? cashSessionId : null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['expenses'] })
      void queryClient.invalidateQueries({ queryKey: ['cash-session'] })
      onClose()
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError ? cause.message : 'Não foi possível lançar.'
      )
    },
  })

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title={salary === null ? 'Lançar gasto' : `Pagar ${salary.name}`}
        description={
          salary === null
            ? 'O que a loja gastou: marmita, bobina, material. Entra no resumo do mês.'
            : `Folha de ${periodLabel(period)}. Pode pagar uma parte: o que faltar continua na folha.`
        }
        className="max-w-lg"
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            if (blocker !== null) return
            setError(null)
            save.mutate()
          }}
        >
          {salary === null ? null : (
            <p className="rounded-(--radius-md) border border-(--border-subtle) px-3 py-2 text-(length:--text-sm)">
              {salary.dueAmount === null
                ? 'Sem salário definido neste mês.'
                : `A receber ${money(salary.dueAmount)} · pago ${money(salary.paidAmount)} · falta `}
              {salary.remainingAmount === null ? null : (
                <strong>{money(salary.remainingAmount)}</strong>
              )}
            </p>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Valor" required>
              {(field) => (
                <MoneyInput
                  {...field}
                  value={amount}
                  onValueChange={setAmount}
                />
              )}
            </Field>
            <Field label="Dia" required>
              {(field) => (
                <DateField
                  {...field}
                  value={spentOn}
                  onChange={(event) => {
                    setSpentOn(event.target.value)
                  }}
                />
              )}
            </Field>
          </div>

          <Field label="O que foi" required>
            {(field) => (
              <Input
                {...field}
                value={description}
                maxLength={200}
                placeholder={
                  salary === null ? 'Marmita, bobina, café…' : undefined
                }
                onChange={(event) => {
                  setDescription(event.target.value)
                }}
              />
            )}
          </Field>

          {salary === null ? (
            <>
              <Field label="Categoria" required>
                {(field) => (
                  <Select
                    {...field}
                    value={categoryId}
                    onChange={(event) => {
                      setCategoryId(event.target.value)
                    }}
                  >
                    <option value="">Escolha…</option>
                    {activeCategories.map((row) => (
                      <option key={row.id} value={row.id}>
                        {row.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              {canManage ? (
                <div className="flex items-end gap-2">
                  <Field label="Nova categoria" className="flex-1">
                    {(field) => (
                      <Input
                        {...field}
                        value={newCategory}
                        maxLength={60}
                        onChange={(event) => {
                          setNewCategory(event.target.value)
                        }}
                      />
                    )}
                  </Field>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={newCategory.trim() === ''}
                    loading={addCategory.isPending}
                    onClick={() => {
                      addCategory.mutate()
                    }}
                  >
                    Criar
                  </Button>
                </div>
              ) : null}

              <Field
                label="Para um funcionário"
                hint="Opcional. Só fica anotado de quem foi, a não ser que desconte do salário."
              >
                {(field) => (
                  <Select
                    {...field}
                    value={employeeId}
                    onChange={(event) => {
                      setEmployeeId(event.target.value)
                      if (event.target.value === '') setDeducts(false)
                    }}
                  >
                    <option value="">Ninguém — gasto da loja</option>
                    {activeEmployees.map((row) => (
                      <option key={row.id} value={row.id}>
                        {row.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              {employeeId === '' ? null : (
                <CheckboxField
                  id="expense-deducts"
                  checked={deducts}
                  onCheckedChange={(checked) => {
                    setDeducts(checked === true)
                  }}
                  label={
                    remainingLabel === null
                      ? `Descontar do salário de ${periodLabel(period)}`
                      : `Descontar do salário de ${periodLabel(period)} (falta ${money(remainingLabel)})`
                  }
                />
              )}
            </>
          ) : null}

          <Field label="Pago com" required>
            {(field) => (
              <RadioGroup
                id={field.id}
                aria-describedby={field['aria-describedby']}
                orientation="horizontal"
                value={source}
                onValueChange={setSource}
                options={[
                  {
                    value: 'cash',
                    label: 'Dinheiro do caixa',
                    description: 'Sai da gaveta aberta.',
                  },
                  {
                    value: 'other',
                    label: 'Pix, banco ou outro',
                    description: 'Não mexe no caixa.',
                  },
                ]}
              />
            )}
          </Field>

          {error === null ? null : (
            <InlineAlert tone="danger">{error}</InlineAlert>
          )}

          <DialogFooter className="items-center justify-between">
            <span
              role={blocker === null ? undefined : 'status'}
              className="text-(length:--text-xs) text-(--text-muted)"
            >
              {blocker ?? ''}
            </span>
            <span className="flex gap-2">
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={blocker !== null}
                loading={save.isPending}
              >
                {salary === null ? 'Lançar' : 'Registrar pagamento'}
              </Button>
            </span>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
